'use client'

import classnames from 'classnames'
import { type FC, useId, useMemo } from 'react'

import { getUserAgnosticGlobalFeatureGatesFromMemory } from '@src/modules/global-state/user-agnostic-feature-gates.browser'
import type { SvdVda } from '@src/quince-site-common/src/domains/svd/svd-product.types'
import { useOptimisticSelection } from '@src/quince-site-common/src/hooks/use-optimistic-selection'
import styles from '@src/quince-site-pdp/src/atoms/optionSelector/optionSelector.module.scss'

import { VDA_TYPE_VS_OPTION_RENDERER, type SvdOptionProp } from './svd-option-renderers'

/**
 * SVD-native option selector — the SVD counterpart of the legacy PDP `OptionSelector`, built on the same
 * principles (see `quince-site-pdp/.../optionSelector/OptionSelector.tsx`): a type → renderer map
 * (`VDA_TYPE_VS_OPTION_RENDERER`, dispatching by VDA `type` instead of per-value `displayType`), a shared
 * `SvdOptionProp` contract, the same `optionSelector.module.scss` list/wrapper markup, the same
 * `useOptimisticSelection` immediate-highlight behaviour (same Statsig gate), and the same value ordering
 * (backend-intended `order`). Styling and interaction stay at parity with today's PDP option rows, while
 * selection/cascade remain SVD-native (this takes the selected value key + an `onSelect` of value keys).
 *
 * NOT ported (not needed for the SVD buy box yet; add if parity requires): show-more/`optionLimit` excess,
 * mini-PDP list trimming, the B2B size-quantity grid, out-of-stock last-chance sizes,
 * `renderAfterColorComponent`. The renderer-map architecture leaves room to add these without a rewrite.
 * (The on-sale red-dot IS supported via `redDotValueKeys`, computed by the caller for the primary swatch.)
 */

interface SvdOptionSelectorProps {
  vda: SvdVda
  selectedValueKey?: string
  /** Value keys still available after cascade (from svdCascadeHelper). Undefined → show all. */
  availableValueKeys?: string[]
  onSelect: (valueKey: string) => void
  className?: string
  /** QA/bot locator stamped on the first option (parity with legacy `OptionSelector`). */
  selectorKey?: string
  /** Value keys that should render the on-sale red-dot (primary-swatch only; computed by the caller). */
  redDotValueKeys?: Set<string>
}

export function SvdOptionSelector({
  vda,
  selectedValueKey,
  availableValueKeys,
  onSelect,
  className,
  selectorKey,
  redDotValueKeys,
}: SvdOptionSelectorProps) {
  const selectionSetId = useId()

  // Same optimistic-selection behaviour as the legacy selector: paint the picked value immediately and
  // defer the heavy recompute. Gated behind the same Statsig flag; a synchronous passthrough when off.
  const { pdp_optimistic_option_selection: isOptimisticSelectionEnabled } =
    getUserAgnosticGlobalFeatureGatesFromMemory()
  const { effectiveSelected, handleSelect } = useOptimisticSelection(
    selectedValueKey,
    onSelect,
    isOptimisticSelectionEnabled,
  )

  // Display order follows the value's `order` (backend-intended sequence, e.g. XS,S,M,L) — mirroring how
  // the legacy flow renders `productOptions.values` in the order the BFF delivers. Array.sort is stable,
  // so equal `order` keeps wire order. Then narrow to the cascade-available value keys.
  const values = useMemo(() => {
    const available = availableValueKeys ? new Set(availableValueKeys) : undefined
    return [...vda.values].sort((a, b) => a.order - b.order).filter(value => !available || available.has(value.key))
  }, [vda.values, availableValueKeys])

  // `vda.type` is a raw wire string. Parity with legacy `OptionSelector` (which filters out values whose
  // `displayType` has no renderer): if this VDA's type has no renderer, drop it — render nothing rather than
  // guess a fallback. NOTE: SVD dispatches per VDA *type*, so this drops the whole VDA (a variant dimension),
  // not a single value — so an unmapped type makes that dimension unselectable (cascade can't disambiguate it,
  // ATC's "exactly one variant" may never resolve). Only reachable for a future/unknown type (all current
  // VdaTypes are mapped). Kept after all hooks so hook order stays unconditional.
  const rendererByType: Partial<Record<string, FC<SvdOptionProp>>> = VDA_TYPE_VS_OPTION_RENDERER
  const OptionRenderer = rendererByType[vda.type]
  if (!OptionRenderer) return null

  return (
    <div
      className={classnames(className, styles.optionListWrapper)}
      data-content-type="option-selector"
      data-option-type={vda.key}
    >
      {values.map((value, index) => {
        const isSelected = value.key === effectiveSelected
        return (
          <div
            key={value.key}
            data-content-type="option-wrapper"
            data-dd-action-name="product option"
            data-value={value.key}
            data-is-selected={isSelected}
            data-selector-key={index === 0 ? selectorKey : undefined}
            className={classnames(styles.optionWrapper, styles.optionWrapper_newDesignV2)}
          >
            <OptionRenderer
              value={value}
              isSelected={isSelected}
              selectionSetId={selectionSetId}
              onChange={handleSelect}
              showRedDot={redDotValueKeys?.has(value.key)}
            />
          </div>
        )
      })}
    </div>
  )
}
